import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { Form, Input, Button, message, Modal, Spin, notification, Typography, Alert } from "antd";
import styled from "styled-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { editCompanyBranch, generateClaimLink, getCompany, uploadBranchLogo, uploadBranchBanner } from "../../../service/client";
import { MediaEditor } from "../../molecules/media-editor/MediaEditor";
import { useBranchContext } from "../../../contexts/branch-context";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { StyledForm, PillTabs } from "../../../styles/shared-components";
import { WarningCircle, X, Link, Copy } from "@phosphor-icons/react";
import { colors } from "../../../styles/theme";
import { useImpersonation } from "../../../contexts/impersonation-context/ImpersonationContext";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const { TextArea } = Input;

const libraries: ("places" | "geometry" | "drawing")[] = ["geometry", "places", "drawing"];
const GOOGLE_MAPS_API_KEY = "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs";

const AddressInputWrapper = styled.div`
  position: relative;

  .pac-container {
    border-radius: 8px;
    border: 1px solid var(--color-border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    margin-top: 4px;
  }
`;

const UnsavedChangesModalContent = styled.div`
  text-align: center;
  padding: 16px 0;

  .icon {
    color: var(--color-warning);
    margin-bottom: 16px;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: var(--color-text);
  }

  .subtitle {
    color: var(--color-text-secondary);
    font-size: 14px;
  }
`;

const SlugPrefix = styled.span`
  color: ${colors.textSecondary};
  font-size: 14px;
  white-space: nowrap;
  user-select: none;
`;

const LogoUploadArea = styled.div<{ hasImage: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 2px dashed var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  background: var(--color-surface);
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--color-primary);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
    text-align: center;
    padding: 8px;
  }
`;

const LogoRemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 1;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const BannerUploadArea = styled.div<{ hasImage: boolean }>`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  border: 2px dashed var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  background: var(--color-surface);
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--color-primary);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
    text-align: center;
    padding: 8px;
  }
`;

const DestructiveButton = styled(Button)`
  &.ant-btn {
    color: var(--color-error);
    border-color: var(--color-error);

    &:hover {
      color: var(--color-surface) !important;
      background-color: var(--color-error) !important;
      border-color: var(--color-error) !important;
    }
  }
`;

const MENU_APP_URL = process.env.REACT_APP_MENU_APP_URL || 'http://localhost:3000';

interface InvitationTabContentProps {
  branchId?: number;
  claimToken: string | null;
  generating: boolean;
  onGenerate: () => void;
}

const InvitationTabContent: React.FC<InvitationTabContentProps> = ({
  branchId,
  claimToken,
  generating,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const { data: company } = useQuery(["/company"], getCompany);

  const buildClaimUrl = () => {
    if (!claimToken || !company?.slug) return null;
    const branchSlug = currentBranch?.slug;
    const path = branchSlug ? `/${company.slug}/${branchSlug}` : `/${company.slug}`;
    return `${MENU_APP_URL}${path}?claim=${claimToken}`;
  };

  const claimUrl = buildClaimUrl();

  const handleCopy = () => {
    if (claimUrl) {
      navigator.clipboard.writeText(claimUrl);
      message.success(t("branchInfo.linkCopiedToClipboard"));
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t("branchInfo.demoForClient")}
        description={t("branchInfo.demoForClientDescription")}
      />

      {claimToken ? (
        <div>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("branchInfo.linkActive")}
            description={t("branchInfo.linkActiveDescription")}
          />
          <Input
            value={claimUrl || ''}
            readOnly
            addonAfter={
              <Button type="text" size="small" icon={<Copy size={14} />} onClick={handleCopy} style={{ margin: '-4px -11px', height: '30px', padding: '0 8px' }}>
                {t("branchInfo.copy")}
              </Button>
            }
            style={{ marginBottom: 12, fontFamily: 'monospace', fontSize: 12 }}
          />
          <Button
            icon={<Link size={14} />}
            onClick={onGenerate}
            loading={generating}
            disabled={!branchId}
          >
            {t("branchInfo.generateNewLink")}
          </Button>
        </div>
      ) : (
        <Button
          type="primary"
          icon={<Link size={14} />}
          onClick={onGenerate}
          loading={generating}
          disabled={!branchId}
        >
          {t("branchInfo.generateInvitationLink")}
        </Button>
      )}
    </div>
  );
};

export interface BranchInfoRef {
  hasUnsavedChanges: () => boolean;
  showUnsavedChangesModal: (onDiscard: () => void) => void;
}

export const BranchInfo = forwardRef<BranchInfoRef>((props, ref) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { currentBranch, availableBranches } = useBranchContext();
  const isMultiBranch = availableBranches.length > 1;
  const { data: company } = useQuery(["/company"], getCompany);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("basic");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoEditorVisible, setLogoEditorVisible] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const uploadedLogoUrlRef = useRef<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerEditorVisible, setBannerEditorVisible] = useState(false);
  const [selectedBannerFile, setSelectedBannerFile] = useState<File | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const uploadedBannerUrlRef = useRef<string | null>(null);
  const [unsavedModalVisible, setUnsavedModalVisible] = useState(false);
  const [onDiscardCallback, setOnDiscardCallback] = useState<(() => void) | null>(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [addressValue, setAddressValue] = useState<string>('');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isDeveloper } = useImpersonation();
  const [claimToken, setClaimToken] = useState<string | null>(
    (currentBranch as any)?.claimToken ?? null
  );
  const [generatingClaim, setGeneratingClaim] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const populateForm = useCallback(() => {
    if (!currentBranch) return;
    if (uploadedLogoUrlRef.current) {
      setLogoPreview(uploadedLogoUrlRef.current);
    } else {
      const logoUrl = currentBranch.logoUrl || null;
      setLogoPreview(logoUrl && logoUrl.includes('amazonaws.com') ? logoUrl : null);
    }
    if (uploadedBannerUrlRef.current) {
      setBannerPreview(uploadedBannerUrlRef.current);
    } else {
      const bannerImage = (currentBranch as any).bannerImage || null;
      setBannerPreview(bannerImage && bannerImage.includes('amazonaws.com') ? bannerImage : null);
    }
    form.setFieldsValue({
      name: currentBranch.name || "",
      slug: currentBranch.slug || "",
      description: currentBranch.description || "",
      socialFacebook: currentBranch.socialMedia?.facebook || "",
      socialInstagram: currentBranch.socialMedia?.instagram || "",
      socialTiktok: currentBranch.socialMedia?.tiktok || "",
      socialX: currentBranch.socialMedia?.x || "",
      socialYoutube: currentBranch.socialMedia?.youtube || "",
      ogTitle: currentBranch.ogTitle || "",
      ogDescription: currentBranch.ogDescription || "",
      ogImage: currentBranch.ogImage || "",
    });

    if (currentBranch.formattedAddress) {
      setAddressValue(currentBranch.formattedAddress);
    } else if (currentBranch.adress) {
      setAddressValue(`${currentBranch.adress.lat.toFixed(4)}, ${currentBranch.adress.lng.toFixed(4)}`);
    } else {
      setAddressValue('');
    }

    if (currentBranch.adress) {
      setSelectedCoordinates({
        lat: currentBranch.adress.lat,
        lng: currentBranch.adress.lng,
      });
    } else {
      setSelectedCoordinates(null);
    }

  }, [currentBranch, form]);

  useEffect(() => {
    populateForm();
    setClaimToken((currentBranch as any)?.claimToken ?? null);
  }, [populateForm, currentBranch]);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry?.location) {
        const components = place.address_components || [];
        const hasStreetNumber = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("street_number"));
        const hasPremise = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("premise"));
        const hasRoute = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("route"));
        if ((!hasStreetNumber && !hasPremise) || (hasRoute && !hasStreetNumber)) {
          setAddressValue('');
          notification.warning({
            message: t("branchInfo.missingBuildingNumber"),
            description: t("branchInfo.missingBuildingNumberDescription"),
            placement: "bottomRight",
          });
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSelectedCoordinates({ lat, lng });
        setAddressValue(place.formatted_address || '');
        setHasChanges(true);
      }
    }
  };

  const updateMutation = useMutation(
    async (values: any) => {
      if (!currentBranch?.id) throw new Error(t("branchInfo.missingBranchId"));

      const body: any = {
        name: values.name,
        slug: values.slug || null,
        description: values.description || null,
        ogTitle: values.ogTitle || null,
        ogDescription: values.ogDescription || null,
        ogImage: values.ogImage || null,
      };

      const socialMedia: Record<string, string> = {};
      if (values.socialFacebook) socialMedia.facebook = values.socialFacebook;
      if (values.socialInstagram) socialMedia.instagram = values.socialInstagram;
      if (values.socialTiktok) socialMedia.tiktok = values.socialTiktok;
      if (values.socialX) socialMedia.x = values.socialX;
      if (values.socialYoutube) socialMedia.youtube = values.socialYoutube;
      body.socialMedia = Object.keys(socialMedia).length > 0 ? socialMedia : null;

if (selectedCoordinates) {
        body.geographicPoint = {
          lat: selectedCoordinates.lat,
          lng: selectedCoordinates.lng,
        };
        body.formattedAddress = addressValue;
      }

      return await editCompanyBranch({ id: currentBranch.id, data: body });
    },
    {
      onSuccess: () => {
        message.success(t("branchInfo.branchDataUpdated"));
        queryClient.invalidateQueries(["/company-branches"]);
        setHasChanges(false);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.error || error?.message || t("branchInfo.errorUpdatingData")
        );
      },
    }
  );

  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validFormats = ["image/jpeg", "image/png", "image/webp"];
    if (!validFormats.includes(file.type)) {
      message.error(t("branchInfo.allowedFormats"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error(t("branchInfo.fileSizeLimit"));
      return;
    }
    setSelectedLogoFile(file);
    setLogoEditorVisible(true);
    e.target.value = "";
  };

  const handleLogoRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    uploadedLogoUrlRef.current = null;
    setLogoPreview(null);
    try {
      await editCompanyBranch({ id: currentBranch!.id, data: { logoUrl: null } });
    } catch {
      // non-critical, preview already cleared
    }
  };

  const handleLogoConfirm = async (croppedBlob: Blob) => {
    setLogoEditorVisible(false);
    const blobUrl = URL.createObjectURL(croppedBlob);
    uploadedLogoUrlRef.current = blobUrl;
    setLogoPreview(blobUrl);
    setUploadingLogo(true);
    try {
      await uploadBranchLogo(currentBranch!.id, croppedBlob);
      message.success(t("branchInfo.logoUpdated"));
    } catch {
      uploadedLogoUrlRef.current = null;
      setLogoPreview(null);
      URL.revokeObjectURL(blobUrl);
      message.error(t("branchInfo.logoUploadFailed"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validFormats = ["image/jpeg", "image/png", "image/webp"];
    if (!validFormats.includes(file.type)) {
      message.error(t("branchInfo.allowedFormats"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error(t("branchInfo.fileSizeLimit"));
      return;
    }
    setSelectedBannerFile(file);
    setBannerEditorVisible(true);
    e.target.value = "";
  };

  const handleBannerConfirm = async (croppedBlob: Blob) => {
    setBannerEditorVisible(false);
    const blobUrl = URL.createObjectURL(croppedBlob);
    uploadedBannerUrlRef.current = blobUrl;
    setBannerPreview(blobUrl);
    setUploadingBanner(true);
    try {
      await uploadBranchBanner(currentBranch!.id, croppedBlob);
      message.success(t("branchInfo.bannerUpdated"));
    } catch {
      uploadedBannerUrlRef.current = null;
      setBannerPreview(null);
      URL.revokeObjectURL(blobUrl);
      message.error(t("branchInfo.bannerUploadFailed"));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleBannerRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    uploadedBannerUrlRef.current = null;
    setBannerPreview(null);
    try {
      await editCompanyBranch({ id: currentBranch!.id, data: { bannerImage: null } });
    } catch {
      // non-critical
    }
  };

  const handleValuesChange = useCallback(() => {
    if (!hasChanges) {
      setHasChanges(true);
    }
  }, [hasChanges]);

  const autoSave = useCallback(async () => {
    if (!hasChanges || updateMutation.isLoading) return;
    try {
      const values = await form.validateFields();
      await updateMutation.mutateAsync(values);
    } catch {
      // validation failed — don't save
    }
  }, [hasChanges, form, updateMutation]);

  const handleReset = useCallback(() => {
    populateForm();
    setHasChanges(false);
  }, [populateForm]);

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => hasChanges,
    showUnsavedChangesModal: (onDiscard: () => void) => {
      setOnDiscardCallback(() => onDiscard);
      setUnsavedModalVisible(true);
    },
  }));

  if (!currentBranch) {
    return <Spin />;
  }

  return (
    <>
      <StyledForm
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        onBlur={autoSave}
      >
        <PillTabs
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
          items={[
            {
              key: "basic",
              label: t("branchInfo.basicInformation"),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t("branchInfo.branchName")}
                    rules={[{ required: true, message: t("branchInfo.branchNameRequired") }]}
                  >
                    <Input placeholder={t("branchInfo.branchName")} />
                  </Form.Item>

                  {isMultiBranch && (
                    <Form.Item
                      name="slug"
                      label={t("branchInfo.branchWebAddress")}
                      rules={[
                        { pattern: /^[a-z0-9-]+$/, message: t("branchInfo.slugPatternError") },
                        { min: 2, message: t("branchInfo.slugMinError") },
                        { max: 50, message: t("branchInfo.slugMaxError") },
                      ]}
                      normalize={(value: string) => value?.toLowerCase().replace(/\s/g, "-")}
                    >
                      <Input
                        prefix={company?.slug ? <SlugPrefix>proste.menu/{company.slug}/</SlugPrefix> : undefined}
                        placeholder={t("branchInfo.slugPlaceholder")}
                      />
                    </Form.Item>
                  )}

                  <Form.Item name="description" label={t("branchInfo.branchDescription")}>
                    <TextArea placeholder={t("branchInfo.branchDescriptionPlaceholder")} rows={4} />
                  </Form.Item>

                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <Form.Item label={t("branchInfo.branchLogo")} style={{ marginBottom: 0 }}>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: "none" }}
                        onChange={handleLogoFileSelect}
                      />
                      <LogoUploadArea
                        hasImage={!!logoPreview}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {uploadingLogo ? (
                          <Spin />
                        ) : logoPreview ? (
                          <>
                            <img src={logoPreview} alt={t("branchInfo.branchLogo")} onError={() => setLogoPreview(null)} />
                            <LogoRemoveButton onClick={handleLogoRemove}>
                              <X size={12} weight="bold" />
                            </LogoRemoveButton>
                          </>
                        ) : (
                          <div className="placeholder">
                            <span style={{ fontSize: 24 }}>+</span>
                            <span>{t("branchInfo.uploadLogo")}</span>
                          </div>
                        )}
                      </LogoUploadArea>
                    </Form.Item>

                    <Form.Item label={t("branchInfo.bannerImage")} style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: "none" }}
                        onChange={handleBannerFileSelect}
                      />
                      <BannerUploadArea
                        hasImage={!!bannerPreview}
                        onClick={() => bannerInputRef.current?.click()}
                      >
                        {uploadingBanner ? (
                          <Spin />
                        ) : bannerPreview ? (
                          <>
                            <img src={bannerPreview} alt={t("branchInfo.branchBanner")} onError={() => setBannerPreview(null)} />
                            <LogoRemoveButton onClick={handleBannerRemove}>
                              <X size={12} weight="bold" />
                            </LogoRemoveButton>
                          </>
                        ) : (
                          <div className="placeholder">
                            <span style={{ fontSize: 24 }}>+</span>
                            <span>{t("branchInfo.uploadBanner")}</span>
                            <span style={{ fontSize: 11 }}>{t("branchInfo.recommendedFormat43")}</span>
                          </div>
                        )}
                      </BannerUploadArea>
                    </Form.Item>
                  </div>

                  <Form.Item label={t("branchInfo.address")}>
                    <AddressInputWrapper>
                      {isLoaded ? (
                        <Autocomplete
                          onLoad={(autocomplete) => {
                            autocomplete.setFields(["place_id", "geometry", "formatted_address", "address_components"]);
                            autocompleteRef.current = autocomplete;
                          }}
                          onPlaceChanged={handlePlaceChanged}
                          options={{
                            componentRestrictions: { country: 'pl' },
                            types: ['address'],
                          }}
                        >
                          <Input
                            placeholder={t("branchInfo.addressPlaceholder")}
                            value={addressValue}
                            onChange={(e) => {
                              setAddressValue(e.target.value);
                              setHasChanges(true);
                            }}
                          />
                        </Autocomplete>
                      ) : (
                        <Input
                          placeholder={t("branchInfo.loading")}
                          prefix={<Spin size="small" />}
                          disabled
                        />
                      )}
                    </AddressInputWrapper>
                  </Form.Item>
                </>
              ),
            },
            {
              key: "social",
              label: "Social Media",
              children: (
                <>
                  <Form.Item name="socialFacebook" label="Facebook">
                    <Input placeholder="https://facebook.com/..." />
                  </Form.Item>

                  <Form.Item name="socialInstagram" label="Instagram">
                    <Input placeholder="https://instagram.com/..." />
                  </Form.Item>

                  <Form.Item name="socialTiktok" label="TikTok">
                    <Input placeholder="https://tiktok.com/@..." />
                  </Form.Item>

                  <Form.Item name="socialX" label="X">
                    <Input placeholder="https://x.com/..." />
                  </Form.Item>

                  <Form.Item name="socialYoutube" label="YouTube">
                    <Input placeholder="https://youtube.com/@..." />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "seo",
              label: "SEO",
              children: (
                <>
                  <Form.Item
                    name="ogTitle"
                    label={t("branchInfo.ogTitleLabel")}
                    help={t("branchInfo.ogDefaultHelp")}
                  >
                    <Input placeholder={t("branchInfo.ogTitlePlaceholder")} />
                  </Form.Item>

                  <Form.Item
                    name="ogDescription"
                    label={t("branchInfo.ogDescriptionLabel")}
                    help={t("branchInfo.ogDefaultHelp")}
                  >
                    <TextArea placeholder={t("branchInfo.ogDescriptionPlaceholder")} rows={3} />
                  </Form.Item>

                  <Form.Item
                    name="ogImage"
                    label={t("branchInfo.ogImageLabel")}
                    help={t("branchInfo.ogImageHelp")}
                  >
                    <Input placeholder="https://example.com/og-image.jpg" />
                  </Form.Item>
                </>
              ),
            },
            ...(isDeveloper ? [{
              key: "invitation",
              label: t("branchInfo.invitation"),
              children: (
                <InvitationTabContent
                  branchId={currentBranch?.id}
                  claimToken={claimToken}
                  generating={generatingClaim}
                  onGenerate={async () => {
                    if (!currentBranch?.id) return;
                    try {
                      setGeneratingClaim(true);
                      const result = await generateClaimLink(currentBranch.id);
                      setClaimToken(result.claimToken);
                    } catch (err: any) {
                      notification.error({
                        message: t("branchInfo.linkGenerationError"),
                        description: err?.response?.data?.message || t("branchInfo.tryAgain"),
                        placement: "bottomRight",
                      });
                    } finally {
                      setGeneratingClaim(false);
                    }
                  }}
                />
              ),
            }] : []),
          ]}
        />

      </StyledForm>

      <Modal
        open={unsavedModalVisible}
        onCancel={() => setUnsavedModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setUnsavedModalVisible(false)}>
            {t("branchInfo.continueEditing")}
          </Button>,
          <DestructiveButton
            key="discard"
            onClick={() => {
              setUnsavedModalVisible(false);
              handleReset();
              if (onDiscardCallback) {
                onDiscardCallback();
              }
            }}
          >
            {t("branchInfo.discardChanges")}
          </DestructiveButton>,
        ]}
        width={400}
      >
        <UnsavedChangesModalContent>
          <div className="icon">
            <WarningCircle size={48} />
          </div>
          <p>{t("branchInfo.unsavedChanges")}</p>
          <p className="subtitle">{t("branchInfo.unsavedChangesConfirm")}</p>
        </UnsavedChangesModalContent>
      </Modal>

      <MediaEditor
        visible={logoEditorVisible}
        file={selectedLogoFile}
        onCancel={() => setLogoEditorVisible(false)}
        onConfirm={handleLogoConfirm}
      />

      <MediaEditor
        visible={bannerEditorVisible}
        file={selectedBannerFile}
        onCancel={() => setBannerEditorVisible(false)}
        onConfirm={handleBannerConfirm}
        aspectRatio={4 / 3}
      />
    </>
  );
});

BranchInfo.displayName = "BranchInfo";
